import styled from 'styled-components';

import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { clearRxHIPAA, clearRxTerms, hipaa, termsAndConditions } from 'src/app/router/paths';
import { smoothScroll } from 'src/app/utils/smoothScroll';

import {
  DoubleNestedList,
  Main,
  NestedList,
  TabbedList,
  Table,
  TableCell,
  TableHeader,
  TopLevelOrderedList,
} from '../TermsConditions/styles';

const TableOfContentsIds = {
  whatDoesThisPrivacyPolicyCover: 'what_does_this_privacy_policy_cover',
  categoriesOfPersonalInformation: 'categories_of_personal_information',
  howInformationIsCollected: 'how_information_is_collected',
  howInformationIsUsed: 'how_information_is_used',
  disclosureToThirdParties: 'disclosure_to_third_parties',
  informationCorrectionAndUpdate: 'information_correction_and_update',
  informationSafeguard: 'information_safeguard',
  privacyPolicyChanges: 'privacy_policy_changes',
  additionalInformation: 'additional_information',
  californiaPrivacyLaws: 'california_privacy_laws',
  otherStateLawPrivacyRights: 'other_state_law_privacy_rights',
  questionsOrConcerns: 'questions_or_concerns',
};

const TableOfContentsLinkHeader = styled.p`
  color: ${props => props.theme.colors.capsuleGreen60} !important;
`;

export const PrivacyPolicy = (props: { isClearRxPage: boolean }) => {
  const isClearRxPage = props.isClearRxPage || false;

  const handleOnTableOfContentsClick = (elementId: string) => {
    // capsule.com/native/privacy is used for mobile and does not need an offset since there is no top navigation bar.
    const currentPath = window?.location?.pathname;
    if (currentPath && currentPath.includes('/native/')) {
      smoothScroll(elementId, 0);
    } else {
      // the -80 offset is for the top navigation bar for the capsule.com/privacy website.
      smoothScroll(elementId, -80);
    }
  };

  return (
    <Main>
      <h1 className="h3">Capsule Privacy Policy</h1>
      <h2 className="font-up-2">Effective Date: April 1, 2025</h2>
      <p>
        To enable us to provide you with a delightful pharmacy experience, we may collect or you may be asked to share
        personal information with us. Capsule is committed to safeguarding the privacy and security of your personal
        information that you or others provide to us. This Capsule Privacy Policy describes the privacy practices of
        Capsule Corporation and all of its subsidiaries (“Capsule”, “we”, or “us”)
      </p>
      <p>
        <strong>
          By using or accessing Capsule’s pharmacy services and programs (both online and in person), Capsule’s website,
          mobile application including social media, products, and other technology platforms (collectively, the
          “Services”), you understand and agree that your personal information will be subject to the practices and
          policies outlined in this Privacy Policy, and you consent to the collection, use, and sharing of your personal
          information in the ways outlined in this Privacy Policy
        </strong>
        . If you don’t agree to this Privacy Policy, you shouldn’t and aren’t permitted to use or access any of the
        Services. You may print a copy of this Privacy Policy by clicking{' '}
        <a href={'#'} onClick={() => window.print()}>
          here
        </a>
        . If you have a disability, you may access this Privacy Policy in an alternative format by contacting
        privacy@capsule.com.
      </p>
      <p>
        Certain information that Capsule collects may be considered “protected health information” or “PHI” under the
        Health Insurance Portability and Accountability Act (“HIPAA”). This Privacy Policy does not cover our
        collection, use or protection of such PHI. Please make sure to carefully review our{' '}
        <LinkWithQuery to={isClearRxPage ? clearRxHIPAA.toPath() : hipaa.toPath()}>
          HIPAA Notices of Privacy Practices
        </LinkWithQuery>{' '}
        (“HIPAA Notice”) which instead applies to the protection and use of PHI.
      </p>
      <p>
        Use of the Services is also subject to our{' '}
        <LinkWithQuery to={isClearRxPage ? clearRxTerms.toPath() : termsAndConditions.toPath()}>
          Terms of Service
        </LinkWithQuery>{' '}
        (our “Terms”), which incorporates this Privacy Policy.
      </p>
      <p>This Privacy Policy is effective with respect to you as of the Effective Date.</p>

      <h2 className="font-up-2">
        <u>Table of Contents</u>
      </h2>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.whatDoesThisPrivacyPolicyCover)}>
        <TableOfContentsLinkHeader>What does this Privacy Policy cover?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.categoriesOfPersonalInformation)}>
        <TableOfContentsLinkHeader>What categories of personal information do we collect?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.howInformationIsCollected)}>
        <TableOfContentsLinkHeader>
          How, when, and why do we collect personal information about you?
        </TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.howInformationIsUsed)}>
        <TableOfContentsLinkHeader>How do we process your personal information?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.disclosureToThirdParties)}>
        <TableOfContentsLinkHeader>
          Why and how do we disclose your personal information we collect to third parties?
        </TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.informationCorrectionAndUpdate)}>
        <TableOfContentsLinkHeader>How do you correct and update your personal information?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.informationSafeguard)}>
        <TableOfContentsLinkHeader>How do we safeguard your personal information?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.privacyPolicyChanges)}>
        <TableOfContentsLinkHeader>Will this Privacy Policy change?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.additionalInformation)}>
        <TableOfContentsLinkHeader>What else do you need to know?</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.californiaPrivacyLaws)}>
        <TableOfContentsLinkHeader>California Privacy Laws</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.otherStateLawPrivacyRights)}>
        <TableOfContentsLinkHeader>Other State Law Privacy Rights</TableOfContentsLinkHeader>
      </a>
      <a onClick={() => handleOnTableOfContentsClick(TableOfContentsIds.questionsOrConcerns)}>
        <TableOfContentsLinkHeader>Questions or Concerns</TableOfContentsLinkHeader>
      </a>

      <TopLevelOrderedList>
        <li>
          <h2 id={TableOfContentsIds.whatDoesThisPrivacyPolicyCover} className="font-up-2">
            1. What does this Privacy Policy cover?
          </h2>

          <p>
            This Privacy Policy covers how we treat your “personal information”, which is identifiable information about
            you. This includes information referred to as “personally identifiable information”, “personal data”, or
            “personal information” under data privacy laws, rules, and regulations that protect you and that apply to
            Capsule.
          </p>
          <p>When we say, “you” we mean users of Capsule’s Services.</p>
          <p>
            This Privacy Policy doesn’t cover the practices of third parties that we don’t own, control, or manage
            including any third-party websites or services. While we try to only work with third parties that share our
            respect for your privacy, we don't take responsibility for their policies, so we encourage you to carefully
            review the privacy policies of the third-party websites or services that you access.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.categoriesOfPersonalInformation} className="font-up-2">
            2. What categories of personal information do we collect?
          </h2>
          <p>
            We may collect the following categories of personal information from you when you visit our website before
            becoming a customer of Capsule by submitting information regarding a prescription or if you only purchase
            non-prescription products from Capsule:
          </p>
          <TabbedList>
            <li>
              Identifiers (such as name, personal email address, personal zip code, and driver’s license, or other forms
              of identification);
            </li>

            <li>Commercial information (such as transaction history);</li>

            <li>Internet or other network or device activity (such as IP address or usage data); </li>

            <li>The general location of the device that you are using to access the Services;</li>

            <li>Legally protected classifications (such as gender);</li>

            <li>Voice recordings made during calls with our pharmacy experience team; and</li>

            <li>
              Other information that identifies or can be reasonably associated with you that you have voluntarily
              provided to us.
            </li>
          </TabbedList>
        </li>

        <li>
          <h2 id={TableOfContentsIds.howInformationIsCollected} className="font-up-2">
            3. How, when, and why do we collect personal information about you?
          </h2>
          <p>We may collect personal information from you from the following sources:</p>
          <p>
            <strong>From or on behalf of You.</strong> We collect personal information about you when you visit our
            website, communicate with us, or otherwise make use of or interact with any of the Services. If a family
            member or another authorized representative is using the Services on your behalf, we may also collect
            information about you through them.
          </p>
          <p>
            <strong>From Your Use of the Services or Interaction with Capsule.</strong> When you visit our website, we
            may gather personal information about your device’s general location, either from your IP address or from
            your use of the Services, or from location-based personal information that you elect to provide through your
            use of a mobile or other Internet-enabled device. We may also receive personal information through Cookies
            (as defined below).
          </p>
          <p>
            <strong>From Our Vendors and Business Partners.</strong> We may receive personal information about you from
            our vendors or business partners who assist us with, for example, marketing or promotional services,
            advertisements, communications, or other personal information gathering. We may use analytics providers to
            collect and analyze personal information about how you interact and engage with the Services.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.howInformationIsUsed} className="font-up-2">
            4. How do we process your personal information?
          </h2>
          <p>
            We may use, store, summarize, disclose, or otherwise handle your personal information we collect about you
            in the following ways:
          </p>
          <TabbedList>
            <li>
              To provide you with the Services you’ve requested and to personalize your experience including:
              <NestedList>
                <li>
                  To process your orders for non-prescription products including any applicable discounts (note that
                  prescription medication orders are covered by the HIPAA Notice);
                </li>

                <li>To respond to your service requests;</li>

                <li>
                  To respond to communications that we receive from you, to contact you when necessary or requested, and
                  to send you personal information about Capsule or the Services; and
                </li>

                <li>
                  To provide you with information that we believe will be of value to you in protecting and managing
                  your health, which may be customized based on orders you’ve filled at Capsule or other information
                  that you’ve provided us.
                </li>
              </NestedList>
            </li>
            <li>
              For any legitimate business purpose including:
              <NestedList>
                <li>
                  To improve the Services, including, for example, testing, internal analytics, and product development;
                </li>

                <li>To market our Services;</li>

                <li>To create aggregated and de-identified data; and</li>

                <li>To provide any legitimate business service or product.</li>
              </NestedList>
            </li>
            <li>
              For legal matters including:
              <NestedList>
                <li>
                  To fulfill our legal obligations under applicable law, regulation, court order or other legal process;
                </li>

                <li>
                  To protect the rights, property, security or safety of you, Capsule, users of the Services, or the
                  public, including detecting and protecting against error, fraud and other unauthorized or illegal
                  activities; and
                </li>

                <li>
                  To resolve any disputes and enforce our Terms and any other agreements or matters with you or others.
                </li>
              </NestedList>
            </li>
            <li>
              To consider, prepare for or carry out a corporate transaction including:
              <NestedList>
                <li>
                  An acquisition of Capsule, or one or more of our business units or of some or all of our assets; and
                </li>

                <li>
                  In the context of some other business acquisition transaction or other transaction in which a third
                  party combines or collaborates with us or one or more of our business units.
                </li>
              </NestedList>
            </li>
          </TabbedList>
        </li>

        <li>
          <h2 id={TableOfContentsIds.disclosureToThirdParties} className="font-up-2">
            5. Why and how do we disclose your personal information we collect to third parties?
          </h2>
          <p>
            We disclose your personal information if you’ve given us permission to do so or as listed in this Privacy
            Policy.
          </p>
          <p>
            We do not sell your personal information to any third party for monetary or other valuable compensation.
          </p>
          <p>
            We may disclose your personal information to the following categories of third parties for the following
            reasons:
          </p>
          <TabbedList>
            <li>
              To our vendors and business partners who have been engaged to enable us to provide the Services that
              you’ve requested, to personalize your experience, or perform business functions on our behalf, including
              our advertising partners who help us market our services and provide you with other offers that may be of
              interest to you;
            </li>

            <li>
              To a law enforcement agency or other government agency, a court or other tribunal, attorney, or a third
              party for the legal matters and corporate transactions described in Section 4 above;
            </li>

            <li>
              To a third party if such disclosure is required in order for us to fulfill our legal obligations under
              applicable law, regulation, court order, or other legal process;
            </li>

            <li>To establish or exercise our legal rights or to defend against claims; and</li>

            <li>To protect the rights, property, security, or safety of you, Capsule, our customers, or the public.</li>
          </TabbedList>
        </li>

        <li>
          <h2 id={TableOfContentsIds.informationCorrectionAndUpdate} className="font-up-2">
            6. How do you correct and update your personal information?
          </h2>
          <p>
            You can correct or update your personal information by updating your account through the Services or by
            contacting us at privacy@capsule.com or (888) 910-1808. It may take us up to thirty days to process your
            request.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.informationSafeguard} className="font-up-2">
            7. How do we safeguard your information?
          </h2>
          <p>
            Capsule follows generally accepted industry practices for protecting your personal information, including
            encryption of sensitive data at rest and in transit, anti-malware detection and prevention mechanisms, and
            real-time monitoring and response for potential security threats to protect personal information. Although
            we work to protect the security of your account and information, please be aware that no method of personal
            information transfer over the Internet, through a system, or electronic data storage is completely secure
            and therefore we can’t guarantee absolute security.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.privacyPolicyChanges} className="font-up-2">
            8. Will this Privacy Policy change?
          </h2>
          <p>
            We may change this Privacy Policy at any time. However, we’ll give you prior notice of any major changes by
            placing a notice on the Services, by sending you an email, or by some other manner, and we’ll let you know
            when the modified Privacy Policy will become effective. Your continued use of the Services after the new
            effective date will be considered assent to the new Privacy Policy.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.additionalInformation} className="font-up-2">
            9. What else do you need to know?
          </h2>
          <p>
            <strong>Internet and Other Electronic Network Activity Tracking</strong>. Capsule and our third-party
            service providers use tracking technologies (collectively, “Cookies”) and browser local storage technology
            to manage your identity, store your preferences, track how you interact with the Services, and make your
            experience more consistent. You can control how local storage works on your browser settings; however,
            disabling the local storage functionality on your browser may prevent you from being able to access and use
            our website.
          </p>
          <p>
            Website tracking technologies are small pieces of data placed on your computer, tablet, phone or similar
            device when you use that device to access the Services. Cookies help us in many ways to make your use of the
            Services more enjoyable and meaningful. We may use these types of Cookies to enable our servers to recognize
            your web browser, tell us how and when you visit and use the Services, analyze trends, learn about our
            customer base, and operate and improve the Services (local storage may also perform these functions).
          </p>
          <p>
            We may use certain types of Cookies like pixels, which are embedded on unauthenticated pages of our website
            (which are pages for which you have not provided login credentials) in order to track and provide
            advertising. We may also use Cookies in our relationships with our vendors and business partners to assist
            us in better understanding customers of our Services through our customers’ interactions with the Services.
          </p>
          <p>
            Most browsers have an option for turning off the Cookie feature, which will prevent your browser from
            accepting new Cookies, as well as (depending on the sophistication of your browser software) allow you to
            decide on acceptance of each new Cookie in a variety of ways, however some Cookies such as those enabling
            site functionality may not be disabled. You can also delete all Cookies that are already on your device. If
            you do this, however, you may have to manually adjust some preferences every time you visit our website and
            other Services and it may impact the overall functionality of some features of the Services. Please note
            that the Services don’t support “Do Not Track” requests sent from a browser at this time.
          </p>
          <p>
            <strong>Children’s Privacy.</strong> The Services are not directed to or intended for use by minors and
            Capsule doesn’t knowingly collect personal information directly from children. If you’re under the age of
            eighteen, please don’t use or access the Services unless you are emancipated or you have the right under
            your state’s law to make decisions about your healthcare, but no one under the age of thirteen is permitted
            to use the Services. However, parents or guardians are permitted to use the Services on behalf of a child
            under their care and these parents or guardians may provide us with their child’s personal information.
            Consistent with the requirements of the US Children’s Online Privacy Protection Act, if we learn that we
            have received personal information directly from a child under age thirteen without his or her parent’s or
            legal guardian’s verified consent, we will use that personal information only to respond directly to that
            child (or his or her parent or legal guardian) to inform the child that he or she cannot use the Services.
            If you believe that a child under thirteen years of age may have directly provided their personal
            information to us, please contact us at privacy@capsule.com and we will investigate the report. We will make
            commercially reasonable efforts to delete personal information of a child under age thirteen without
            parental or guardian permission.
          </p>
          <p>
            <strong>Information Retention.</strong> We retain personal information about you for as long as necessary to
            provide you with our Services and to perform our processing activities. When establishing a retention period
            for specific categories of personal information, we consider who we collected the personal information from,
            our need for the personal information, and why we collected the personal information. In some cases, we
            retain personal information for a long period if doing so is necessary to comply with our legal obligations,
            resolve disputes or collect fees owed, or is otherwise permitted or required by applicable law, rule or
            regulation. We may further retain information as necessary to carry out our ongoing obligations or
            activities, including for example in legal matters, and in an anonymous or aggregated form where that
            information would not reasonably identify you personally.
          </p>
        </li>

        <li>
          <h2 id={TableOfContentsIds.californiaPrivacyLaws} className="font-up-2">
            10. For California Residents
          </h2>
          <p>
            If you’re a California resident, California law requires us to provide you with additional information about
            how we collect, use, share, and otherwise process your personal information. If there are any conflicts
            between this section and any other provision of this Privacy Policy and you’re a California resident, the
            portion that is more protective of your personal information shall control to the extent of such conflict.
            This section provides you with our privacy practices in addition to those above, but only applies to your
            personal information included in the CCPA’s scope; it does not apply to PHI, which is covered by the HIPAA
            Notice.
          </p>
          <p>
            For California personnel of Capsule, please see Capsule’s human resources information system for the privacy
            policy notice applicable to California personnel and for California candidates for positions at Capsule,
            please refer to the privacy policy notice in the job posting.
          </p>
          <p>
            <strong>Personal Information We Collect.</strong>In addition to the information we collect listed above, we
            also collect the following personal information from the following categories of data subjects:
          </p>
          <TabbedList>
            <li>
              <strong>If you are a health care provider</strong>, including a physician, nurse practitioner, or other
              health care professional using our Services (collectively, “Health Care Providers”), we may also collect
              the following categories of personal information when you use our Services:
              <NestedList>
                <li>
                  Health Care Provider contact data and other professional information (such as your name, your clinic
                  or hospital name, phone email, and office address) and provider identifiers (such as NPI number and
                  DEA number);
                </li>

                <li>
                  Practice information (such as type of practice, specialty, names of office staff or other contacts at
                  the practice); and
                </li>

                <li>Your prescription orders and related communications.</li>
              </NestedList>
              We may also collect your personal information from publicly available sources, third parties, such as
              other health care providers who recommend you as someone who may be interested in Capsule, and third party
              data collectors who license health care provider information to us.
            </li>

            <li>
              <strong>If you are an individual only making non-prescription purchases</strong>, we may also collect the
              following categories of personal information:
              <NestedList>
                <li>Your products ordered and delivered and related communications; and</li>

                <li>Your delivery address, phone number, email address, and other contact information.</li>
              </NestedList>
            </li>

            <li>
              <strong>If you are our business contact or potential business contact</strong>, we may also collect the
              following categories of personal information to seek business or conduct business with you or the party
              you work for:
              <NestedList>
                <li>The company(ies) or groups you are affiliated with and your role(s);</li>

                <li>Your business contact information; and</li>

                <li>
                  Information related to your business needs, interests in our products or services, and communication
                  with our team.
                </li>
              </NestedList>
            </li>
          </TabbedList>
          <p>
            <strong>Personal Information Disclosed for a Business Purpose</strong>. We have disclosed the following
            categories of personal information to following categories of third parties in the preceding twelve months.
            Capsule does not “sell” or “share” personal information as those words are defined by CCPA.
          </p>
          <Table>
            <thead>
              <TableHeader>Categories of Personal Information (described above)</TableHeader>
              <TableHeader>Recipients to Whom Personal Information is Disclosed for a Business Purpose</TableHeader>
            </thead>
            <tbody>
              <tr>
                <TableCell>Identifiers (See Section 2 & 10 “Personal Information We Collect”)</TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>Internet or other electronic network activity (See Section 2)</TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>General Location Information (See Section 2)</TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>Legally Protected Classifications (See Section 2)</TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>Voice Recordings (See Section 2)</TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>
                  Health Care Provider information (See Section 10 “Personal Information We Collect”)
                </TableCell>
                <TableCell>
                  Our vendors and business partners, to a law enforcement agency or other government agency, a court or
                  other tribunal, attorney, or a third party for the legal matters, third parties in connection with any
                  corporate transaction, any third party with your consent or at your direction, or as we believe to be
                  necessary and appropriate.
                </TableCell>
              </tr>
              <tr>
                <TableCell>
                  Product orders (that are not prescriptions) and related communications (See Section 10 “Personal
                  Information We Collect”)
                </TableCell>
                <TableCell>
                  Product orders (that are not prescriptions) and related communications (See Section 10 “Personal
                  Information We Collect”)
                </TableCell>
              </tr>
            </tbody>
          </Table>

          <p>
            <strong>Your Privacy Rights.</strong> You have the following rights under the CCPA with respect to your
            personal information.
          </p>

          <p>
            <TabbedList>
              <li>
                <u>Right to Know</u>. You have the right to request details of the personal information about you that
                we’ve collected and used subject to our receipt and confirmation of your verifiable consumer request.
                Specifically, we’ll disclose to you:
                <NestedList>
                  <li>The categories of personal information we collected about you;</li>

                  <li>The categories of sources for personal information we collected about you;</li>

                  <li>Our business or commercial purpose for collecting your personal information;</li>

                  <li>The categories of third parties with whom we shared your personal information;</li>

                  <li>The specific pieces of your personal information that we have collected about you; and</li>

                  <li>
                    If we disclosed your personal information for a business purpose or “shared” (as such terms are
                    defined under the CCPA) your personal information over the past twelve months, a list identifying
                    the personal information categories that each category of third-party recipient obtained or that was
                    sold to each category of third-party recipient.
                  </li>
                </NestedList>
              </li>
              <li>
                <u>Right to Delete</u>. You have the right to request the deletion of your personal information that is
                collected by us subject to our receipt and confirmation of your verifiable consumer request. Under the
                CCPA, this right is subject to certain exceptions. For example, we may need to retain your personal
                information to provide you with the Services or complete a transaction or other action you’ve requested,
                or if deletion of your personal information involves disproportionate effort. If your deletion request
                is subject to an exception, we may deny your deletion request.
              </li>

              <li>
                <u>Right to Correction </u>. You have the right to request that we correct any inaccurate personal
                information we maintain about you. Under the CCPA, this right is subject to certain exceptions. For
                example, if we decided, based on the totality of the circumstances related to your personal information,
                that such data is correct, we’re not required to act on the request. If your correction request is
                subject to an exception, we may deny your request.
              </li>

              <li>
                <u>Right to Opt Out of Processing of Sensitive Personal Information</u>. While California consumers have
                certain rights over the processing of their sensitive information, Capsule does not collect your
                “sensitive personal information” as that term is defined by CCPA.
              </li>

              <li>
                <u>Right to Opt Out of and Opt Into Selling and Sharing</u>. Under the CCPA, California residents have
                certain rights when a business “sells” or “shares” personal information with third parties for purposes
                of cross-contextual behavioral advertising. Capsule does not “sell” personal information, as that term
                is defined by CCPA. If Capsule does sell or share, Capsule may only share or sell personal information
                collected about you from unauthenticated webpages, including the landing page, for advertising purposes,
                and will not share or sell personal information collected after you’ve logged into the Services for such
                purposes.
              </li>
              <p>
                In the last twelve months, we may have shared Internet and Other Electronic Network Activity Tracking
                for the business or commercial purposes stated in the Internet and Other Electronic Network Activity
                Tracking section.
              </p>
              <NestedList>
                <li>
                  You have the right to opt-out of the sale or sharing of your personal information. You can opt-out
                  using the following methods:
                  <DoubleNestedList>
                    <li>Email us at privacy@capsule.com.</li>
                    <li>
                      By implementing the Global Privacy Control or similar control that is legally recognized by a
                      government agency or industry standard and that complies with the CCPA. The signal issued by the
                      control must be initiated by your browser and applies to the specific device and browser you use
                      at the time you cast the signal. Please note this does not include Do Not Track signals.
                    </li>
                  </DoubleNestedList>
                </li>

                <li>
                  Once you have submitted an opt-out request, we will not ask you to reauthorize the sale or sharing of
                  your personal information for at least twelve months.
                </li>

                <li>
                  We do not knowingly sell or share any personal information of minors under sixteen years of age.
                </li>
              </NestedList>
              <li>
                <u>Right to Non-Discrimination</u>. We may not discriminate against you because you’ve exercised any of
                the privacy rights described above.
              </li>
            </TabbedList>
            <p>
              <strong>Exercising Your Rights Under the CCPA</strong>. Except as otherwise provided, to exercise the
              rights above, you, or your authorized agent must send us a request that provides sufficient information to
              allow us to verify that you’re the person about whom we’ve collected personal information and describes
              your request in sufficient detail to allow us to understand, evaluate and respond to it. If you identify
              yourself as an authorized agent of a data subject, you will be asked to provide us with the data subject’s
              signed permission demonstrating that you have been authorized by the data subject to act on their behalf
              or other valid legal documentation confirming your status. Satisfaction of these criteria means your
              request is a “verifiable consumer request”; we’re not required to respond to requests that don’t meet
              these criteria. We’ll only use the personal information provided in a request to verify your identity and
              complete your request. You don’t need an account to submit a request.
            </p>
            <p>
              We’ll work to respond to your request within the time period required by CCPA. We’ll not charge you a fee
              for making a request unless your request(s) is excessive, repetitive, or manifestly unfounded. If we
              determine that your request warrants a fee, we’ll notify you of the fee and explain that decision before
              completing your request.
            </p>
            <p>You may submit a request using the following methods:</p>
            <TabbedList>
              <li>Email us at privacy@capsule.com;</li>

              <li>Call us toll-free at (888) 910-1808; or</li>

              <li>
                In writing to Capsule’s Privacy Officer at 122 W 146th Street, New York, NY 10039 (Attention: Privacy
                Officer and Legal Department).
              </li>
            </TabbedList>
          </p>
        </li>
        <li>
          <h2 id={TableOfContentsIds.otherStateLawPrivacyRights} className="font-up-2">
            11. Other Privacy Rights
          </h2>
          <p>
            Capsule intends to provide you with all privacy rights to which you are entitled by law. If you believe you
            are entitled to exercise privacy rights under any other applicable law, please email us at
            privacy@capsule.com or write to Capsule’s Privacy Officer at 122 W 146th Street, New York, NY 10039
            (Attention: Privacy Officer and Legal Department).
          </p>
        </li>
        <li>
          <h2 id={TableOfContentsIds.questionsOrConcerns} className="font-up-2">
            12. Questions or Concerns
          </h2>
          <p>
            If you’ve any questions or concerns regarding privacy using the Services, please contact us at
            privacy@capsule.com or (888) 910-1808.
          </p>
        </li>
      </TopLevelOrderedList>
    </Main>
  );
};
